import React from "react";
import { NavLink, useParams, useLocation } from "react-router-dom";
import useCsr from "../hooks/useCsr";
import { CSR_STATUS } from "../utils/Constants";

const SidebarCSR = () => {
  const { studyId } = useParams();
  const location = useLocation();
  const { mainStatus } = useCsr();

  const handleClick = e => {
    e.preventDefault();
  };

  const app = location.pathname.split("/")[1];

  let navLinks;
  if (app === "ls") {
    navLinks = [
      { path: "select-study", label: "Select Study", stepNumber: 1 },
      {
        path: "lay-summary-questions",
        label: "Wizard Questions",
        stepNumber: 2,
      },
      {
        path: "lay-summary-file",
        label: "Lay Summary File",
        stepNumber: 3,
      },
    ];
  } else if (app === "pn") {
    navLinks = [
      { path: "summary", label: "Patient Narrative Name", stepNumber: 1 },
      { path: "file-upload", label: "File Upload", stepNumber: 2 },
      {
        path: "project-conventions",
        label: "Wizard Questions",
        stepNumber: 3,
        subItems: [
          {
            stepNumber: 4,
            path: "project-conventions",
            label: "Project & Conventions",
          },
          {
            stepNumber: 5,
            path: "patient-scope",
            label: "Patient Scope",
          },
          {
            stepNumber: 6,
            path: "sources",
            label: "Sources",
          },
          {
            stepNumber: 7,
            path: "narrative-format",
            label: "Narrative Format",
          },
        ],
      },
      { path: "data-mapping", label: "Data Mapping", stepNumber: 8 },
      { path: "file", label: "Patient Narrative File", stepNumber: 9 },
    ];
  } else if (app === "scs") {
    navLinks = [
      { path: "summary", label: "Summary Name", stepNumber: 1 },
      { path: "file-upload", label: "File Upload", stepNumber: 2 },
      {
        path: "description-of-studies",
        label: "Wizard Questions",
        stepNumber: 3,
        subItems: [
          {
            stepNumber: 4,
            path: "description-of-studies",
            label: "Description of Studies",
          },
          {
            stepNumber: 5,
            path: "subject-disposition",
            label: "Subject Disposition",
          },
          {
            stepNumber: 6,
            path: "overall-extent-of-exposure",
            label: "Overall Extent of Exposure",
          },
          {
            stepNumber: 7,
            path: "analysis-of-adverse-events",
            label: "Analysis of Adverse Events",
          },
          {
            stepNumber: 8,
            path: "clinical-laboratory-evaluations",
            label: "Clinical Laboratory Evaluations",
          },
          {
            stepNumber: 9,
            path: "safety-in-special-groups",
            label: "Safety in Special Groups & Situations",
          },
          {
            stepNumber: 10,
            path: "post-marketing-data",
            label: "Post-Marketing Data",
          },
        ],
      },
      { path: "data-mapping", label: "Data Mapping", stepNumber: 11 },
      { path: "scs-file", label: "SCS File", stepNumber: 12 },
    ];
  } else {
    navLinks = [
      { path: "summary", label: "Study Name", stepNumber: 1 },
      { path: "file-upload", label: "File Upload", stepNumber: 2 },
      {
        path: "study-design-demographics",
        label: "Wizard Questions",
        stepNumber: 3,
        subItems: [
          {
            stepNumber: 4,
            path: "study-design-demographics",
            label: "Study Design",
          },
          {
            stepNumber: 5,
            path: "efficacy-analysis",
            label: "Efficacy",
          },
          {
            stepNumber: 6,
            path: "safety-analysis",
            label: "Safety",
          },
          {
            stepNumber: 7,
            path: "stdpharmacokinetics",
            label: "Standard PK",
          },
          {
            stepNumber: 8,
            path: "comppharmacokinetics",
            label: "Comparative PK",
          },
          {
            stepNumber: 9,
            path: "bioavailability",
            label: "Bioavailability",
          },
          {
            stepNumber: 10,
            path: "stdpharmacodynamics",
            label: "Standard PD",
          },
          {
            stepNumber: 11,
            path: "comppharmacodynamics",
            label: "Comparative PD",
          },
        ],
      },
      { path: "data-mapping", label: "Data Mapping", stepNumber: 12 },
      { path: "file", label: "CSR file", stepNumber: 13 },
      { path: "advancedsearch", label: "TriloAI", stepNumber: 14 },
    ];
  }

  const isTriloAiEnabled =
    app === "csr" &&
    studyId &&
    studyId !== "new" &&
    mainStatus === CSR_STATUS.ready;

  const getNavLinkProps = path => {
    let fullPath = "";
    if (app === "pn") {
      fullPath = studyId ? `/pn/${studyId}/${path}` : `/pn/new/${path}`;
    } else if (app === "ls") {
      fullPath = studyId ? `/ls/${studyId}/${path}` : `/ls/new/${path}`;
    } else if (app === "scs") {
      fullPath = studyId ? `/scs/${studyId}/${path}` : `/scs/new/${path}`;
    } else {
      fullPath = studyId ? `/csr/${studyId}/${path}` : `/csr/new/${path}`;
    }

    const isTriloAiLink = app === "csr" && path === "advancedsearch";
    const isDisabled = !studyId || (isTriloAiLink && !isTriloAiEnabled);

    return {
      to: fullPath,
      onClick: isDisabled ? handleClick : undefined,
    };
  };

  const isNavLinkDisabled = path => {
    if (app !== "csr" || path !== "advancedsearch") return false;
    return !isTriloAiEnabled;
  };

  const getTitle = () => {
    if (app === "pn") {
      return studyId ? "Patient Narrative" : "New Patient Narrative";
    } else if (app === "ls") {
      return "Create a new lay summary report";
    } else if (app === "scs") {
      return studyId ? "SCS" : "New SCS";
    } else {
      return studyId ? "CSR" : "New CSR";
    }
  };

  const subItemStepNumbers = new Set();
  navLinks.forEach(({ subItems }) => {
    if (subItems) {
      subItems.forEach(sub => {
        if (sub.stepNumber) subItemStepNumbers.add(sub.stepNumber);
      });
    }
  });

  const mainStepDisplayNumbers = new Map();
  navLinks.forEach((link, index) => {
    mainStepDisplayNumbers.set(link.stepNumber, index + 1);
  });

  const wizardStepNumber = navLinks.find(
    ({ subItems }) => subItems
  )?.stepNumber;

  const getAllSteps = () => {
    const allSteps = [];
    navLinks.forEach(({ stepNumber, subItems }) => {
      allSteps.push({ stepNumber, isSubItem: false });
      if (subItems) {
        subItems.forEach(subItem => {
          if (subItem.stepNumber) {
            allSteps.push({ stepNumber: subItem.stepNumber, isSubItem: true });
          }
        });
      }
    });
    return allSteps.sort((a, b) => a.stepNumber - b.stepNumber);
  };

  const appPathSegment = "(?:csr|ls|scs|pn)";

  const getCurrentStep = () => {
    const currentPath = location.pathname;

    const isTableAnalysisPage =
      currentPath.includes("table-analysis-questions") ||
      currentPath.includes("table-analysis-ai-response");
    if (app === "csr" && isTableAnalysisPage) {
      const advancedSearchIndex = navLinks.findIndex(
        ({ path }) => path === "advancedsearch"
      );
      if (advancedSearchIndex >= 0) {
        return navLinks[advancedSearchIndex].stepNumber;
      }
    }

    for (const navLink of navLinks) {
      if (navLink.subItems) {
        for (const subItem of navLink.subItems) {
          if (subItem.stepNumber) {
            const subPathPattern = new RegExp(
              `/${appPathSegment}/(?:new|[0-9a-fA-F]+)/${subItem.path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(/|$)`
            );
            if (subPathPattern.test(currentPath)) {
              return subItem.stepNumber;
            }
          }
        }
      }
    }

    const stepIndex = navLinks.findIndex(({ path }) => {
      const pathPattern = new RegExp(
        `/${appPathSegment}/(?:new|[0-9a-fA-F]+)/${path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(/|$)`
      );
      return pathPattern.test(currentPath);
    });

    return stepIndex >= 0 ? navLinks[stepIndex].stepNumber : 1;
  };

  const currentStep = getCurrentStep();

  const getStepState = stepNumber => {
    if (stepNumber < currentStep) {
      return "completed";
    } else if (stepNumber === currentStep) {
      return "active";
    } else {
      return "inactive";
    }
  };

  const shouldShowProgressLine = (
    stepNumber,
    isSubItem = false,
    isLastSubItem = false,
    parentHasSubItems = false
  ) => {
    const state = getStepState(stepNumber);
    const allSteps = getAllSteps();
    const maxStepNumber = Math.max(...allSteps.map(s => s.stepNumber));

    if (state === "inactive") {
      return false;
    }

    if (isSubItem) {
      return !isLastSubItem && (state === "completed" || state === "active");
    }

    if (parentHasSubItems && stepNumber === wizardStepNumber) {
      return state === "completed" || state === "active";
    }

    return (
      (state === "completed" || state === "active") &&
      stepNumber < maxStepNumber
    );
  };

  const getDisplayStepNumber = stepNumber => {
    if (subItemStepNumbers.has(stepNumber)) return "";
    return mainStepDisplayNumbers.get(stepNumber) ?? stepNumber;
  };

  const isSubItemActive = subItemPath => {
    const currentPath = location.pathname;
    const subPathPattern = new RegExp(
      `/${appPathSegment}/(?:new|[0-9a-fA-F]+)/${subItemPath.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(/|$)`
    );
    return subPathPattern.test(currentPath);
  };

  return (
    <div className={"sidebar__csr"}>
      <h2 className={"title"}>{getTitle()}</h2>
      <div className={"progress-indicator"}>
        {navLinks.map(({ path, label, stepNumber, subItems }) => (
          <div
            key={path}
            className={subItems ? "progress-step-wrapper--has-sub-items" : ""}>
            <div
              className={`progress-step progress-step--${getStepState(stepNumber)} ${subItems ? "progress-step--has-sub-items" : ""}`}>
              <div className={"progress-step__content"}>
                <div className={"progress-step__icon"}>
                  {getStepState(stepNumber) === "completed" ? (
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="11" fill="#4AB8AA" />
                      <path
                        d="M7 12L10.5 15.5L17 9"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ) : (
                    <div className={"progress-step__number"}>
                      {getDisplayStepNumber(stepNumber)}
                    </div>
                  )}
                </div>
                <NavLink
                  {...getNavLinkProps(path)}
                  className={({ isActive }) => {
                    // The table analysis pages sit under Advanced Search,
                    // so its step stays highlighted while they are open.
                    const active =
                      isActive ||
                      (app === "csr" &&
                        path === "advancedsearch" &&
                        (location.pathname.includes(
                          "table-analysis-questions"
                        ) ||
                          location.pathname.includes(
                            "table-analysis-ai-response"
                          )));
                    return `progress-step__link ${active ? "progress-step__link--active" : ""} ${isNavLinkDisabled(path) ? "progress-step__link--disabled" : ""}`;
                  }}
                  end={false}>
                  {label}
                </NavLink>
              </div>
              {shouldShowProgressLine(stepNumber, false, false, !!subItems) && (
                <div
                  className={`progress-step__line progress-step__line--main ${subItems ? "progress-step__line--with-sub-items" : ""}`}
                  style={
                    subItems
                      ? {
                          // The --with-sub-items SCSS height (calc(100% + 268px))
                          // was tuned for CSR's 8 sub-items under Wizard
                          // Questions; that fixed extension overshoots for any
                          // other sub-item count (e.g. PN's 4), dragging the
                          // line past the last step. 33.5px/sub-item
                          // reproduces the original 268px exactly at 8 items,
                          // so this is a no-op for CSR and scales correctly
                          // for everyone else.
                          height: `calc(100% + ${subItems.length * 33.5}px)`,
                        }
                      : undefined
                  }
                />
              )}
            </div>
            {subItems &&
              subItems.map((subItem, subItemIndex) => {
                const subItemActive = isSubItemActive(subItem.path);
                const subItemStepNumber = subItem.stepNumber;
                const subItemState = subItemStepNumber
                  ? getStepState(subItemStepNumber)
                  : "inactive";
                const isLastSubItem = subItemIndex === subItems.length - 1;

                return (
                  <div key={subItem.path + subItem.label}>
                    <div
                      className={`progress-step progress-step__sub-item progress-step--${subItemState}`}>
                      <div className={"progress-step__content"}>
                        <div className={"progress-step__icon"}>
                          {subItemStepNumber && subItemState === "completed" ? (
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg">
                              <circle cx="12" cy="12" r="11" fill="#4AB8AA" />
                              <path
                                d="M7 12L10.5 15.5L17 9"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : subItemStepNumber ? (
                            <div className={"progress-step__number"}>
                              {getDisplayStepNumber(subItemStepNumber)}
                            </div>
                          ) : null}
                        </div>
                        <NavLink
                          {...getNavLinkProps(subItem.path)}
                          className={({ isActive }) =>
                            `progress-step__link progress-step__link--sub ${
                              isActive || subItemActive
                                ? "progress-step__link--active"
                                : ""
                            }`
                          }
                          end={false}>
                          {subItem.label}
                        </NavLink>
                      </div>
                      {shouldShowProgressLine(
                        subItemStepNumber,
                        true,
                        isLastSubItem
                      ) && (
                        <div
                          className={
                            "progress-step__line progress-step__line--sub"
                          }
                        />
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SidebarCSR;
